/**
 * Utility functions for grouping images by base filename (same content, different dimensions)
 */

/**
 * Extract base filename by removing dimension patterns and extensions
 * Examples:
 * - "medical_heart_1920x1080.jpg" -> "medical_heart"
 * - "business_meeting_800x600.png" -> "business_meeting"
 * - "istock_123456_1024x768.jpg" -> "istock_123456"
 */
export const extractBaseName = (filename) => {
  if (!filename) return '';

  // Remove dimension patterns like _1920x1080, _800x600, etc.
  let baseName = filename.replace(/_\d+x\d+/g, '');

  // Remove file extension
  baseName = baseName.replace(/\.[^.]+$/, '');

  // Clean up any trailing underscores or hyphens
  baseName = baseName.replace(/[-_]+$/, '');

  return baseName;
};

/**
 * Extract dimensions from filename
 * Returns { width, height } or null if no dimensions found
 */
export const extractDimensions = (filename) => {
  if (!filename) return null;

  const match = filename.match(/_(\d+)x(\d+)/);
  if (match) {
    return {
      width: parseInt(match[1], 10),
      height: parseInt(match[2], 10)
    };
  }

  return null;
};

/**
 * Group images by base filename
 * Returns an object where keys are base names and values are arrays of image variants
 */
export const groupImagesByBaseName = (images) => {
  if (!Array.isArray(images)) return {};

  const groups = {};

  images.forEach(image => {
    const baseName = extractBaseName(image.filename);

    if (!groups[baseName]) {
      groups[baseName] = [];
    }

    // Add dimension info to the image object
    const dimensions = extractDimensions(image.filename);
    const imageWithDimensions = {
      ...image,
      extractedDimensions: dimensions,
      baseName: baseName
    };

    groups[baseName].push(imageWithDimensions);
  });

  // Sort images within each group by resolution (highest first)
  Object.keys(groups).forEach(baseName => {
    groups[baseName].sort((a, b) => {
      const aRes = (a.extractedDimensions?.width || a.width || 0) * (a.extractedDimensions?.height || a.height || 0);
      const bRes = (b.extractedDimensions?.width || b.width || 0) * (b.extractedDimensions?.height || b.height || 0);
      return bRes - aRes; // Highest resolution first
    });
  });

  return groups;
};

/**
 * Convert grouped images back to a flat array for display
 * Only includes the representative (highest resolution) image from each group
 */
export const getRepresentativeImages = (groupedImages) => {
  const representatives = [];

  Object.keys(groupedImages).forEach(baseName => {
    const group = groupedImages[baseName];
    if (group.length > 0) {
      // Take the first image (highest resolution due to sorting)
      const representative = {
        ...group[0],
        isGrouped: group.length > 1,
        groupVariants: group,
        variantCount: group.length
      };
      representatives.push(representative);
    }
  });

  return representatives;
};

/**
 * Get all available dimensions for a group of images
 * Returns array of dimension objects sorted by resolution
 */
export const getAvailableDimensions = (imageGroup) => {
  if (!Array.isArray(imageGroup)) return [];

  const dimensions = imageGroup.map(image => {
    const extracted = image.extractedDimensions;
    const width = extracted?.width || image.width || 0;
    const height = extracted?.height || image.height || 0;

    return {
      width,
      height,
      label: `${width}×${height}`,
      resolution: width * height,
      image: image
    };
  });

  // Sort by resolution (highest first)
  return dimensions.sort((a, b) => b.resolution - a.resolution);
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if two images belong to the same group
 */
export const areImagesInSameGroup = (image1, image2) => {
  return extractBaseName(image1.filename) === extractBaseName(image2.filename);
};